import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { useDebouncedValue } from '../../lib/hooks/useDebouncedValue';
import { getEventState, getEventWeeks } from '../../lib/api/event';
import { Modal } from './Modal';
import {
  getWeekLeaderboard,
  disqualifyUser,
  reinstateOnLeaderboard,
  type WeekLeaderboard,
  type AdminStandingRow,
} from '../../lib/api/admin';
import { Pagination } from './Pagination';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const WINNER_CUTOFF = 10; // top N are the prize-eligible winners

/**
 * Leaderboard tab — per-week standings (with contact info for prizing) and
 * per-week disqualification. The top 10 are the prize-eligible winners; the
 * full contact details (email/phone) are shown so ops can reach them.
 * Disqualifying pulls a user from THIS week's public board (reversible) without
 * touching their account.
 */
export function LeaderboardTab({ onOpenUser }: { onOpenUser: (userId: string) => void }) {
  const [weeks, setWeeks] = useState<number[]>([]);
  const [week, setWeek] = useState<number | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState<WeekLeaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingDq, setPendingDq] = useState<AdminStandingRow | null>(null);
  const [reinstatingId, setReinstatingId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      getEventState({ signal: controller.signal }),
      getEventWeeks({ signal: controller.signal }),
    ])
      .then(([state, weeksData]) => {
        const nums = weeksData.weeks.map((w) => w.weekNumber).sort((a, b) => a - b);
        setWeeks(nums);
        setWeek(nums.includes(state.currentWeek) ? state.currentWeek : (nums[0] ?? null));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setBootError(
          err instanceof ApiError ? `Couldn't load event weeks (${err.status}).` : "Couldn't load event weeks.",
        );
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (week == null) return;
    const controller = new AbortController();
    setLoading(true);
    getWeekLeaderboard(
      { week, search: search || undefined, limit: pageSize, offset: (page - 1) * pageSize },
      { signal: controller.signal },
    )
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? `Couldn't load the leaderboard (${err.status}).` : "Couldn't load the leaderboard.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [week, search, page, pageSize, reloadKey]);

  // Clamp page when the result set shrinks (e.g. after a disqualification).
  useEffect(() => {
    if (!data || data.total === 0) return;
    const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
    setPage((cur) => (cur > totalPages ? totalPages : cur));
  }, [data, pageSize]);

  const refetch = (): void => setReloadKey((k) => k + 1);

  const onReinstate = async (userId: string): Promise<void> => {
    if (week == null) return;
    setReinstatingId(userId);
    try {
      await reinstateOnLeaderboard(userId, week);
      refetch();
    } catch {
      // Surface via the main error banner on next fetch; keep it simple.
      setError('Could not reinstate that user. Please try again.');
    } finally {
      setReinstatingId(null);
    }
  };

  if (bootError) return <p className="text-white/80">{bootError}</p>;

  const total = data?.total ?? 0;
  const standings = data?.standings ?? [];
  const excluded = data?.excluded ?? [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-white/55">Week</span>
        {weeks.length === 0 ? (
          <span className="text-sm text-white/50">Loading…</span>
        ) : (
          weeks.map((w) => (
            <button
              key={w}
              type="button"
              aria-pressed={w === week}
              onClick={() => {
                setWeek(w);
                setPage(1);
              }}
              className={[
                'rounded-md border px-3 py-1.5 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                w === week
                  ? 'border-brand-redAccent bg-brand-redAccent/20 text-white'
                  : 'border-white/15 text-white/75 hover:bg-white/10',
              ].join(' ')}
            >
              Week {w}
            </button>
          ))
        )}
      </div>

      <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
        Search this week
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Email or name"
          className="w-64 rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
        />
      </label>

      <p className="text-xs text-white/45">
        The top {WINNER_CUTOFF} (highlighted) are the prize-eligible winners — contact details shown for
        prizing. Disqualifying removes a user from this week&rsquo;s public board only; it&rsquo;s reversible.
      </p>

      {error && <p className="text-white/80">{error}</p>}

      <div
        aria-busy={loading}
        className={`overflow-x-auto rounded-xl border border-white/15 bg-black/20 transition-opacity ${
          loading ? 'opacity-60' : 'opacity-100'
        }`}
      >
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/55">
              <Th>#</Th>
              <Th>Player</Th>
              <Th>Phone</Th>
              <Th>Score</Th>
              <Th>Achieved</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {loading && standings.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-white/50">
                  Loading standings…
                </td>
              </tr>
            ) : standings.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-white/50">
                  No scores for this week yet.
                </td>
              </tr>
            ) : (
              standings.map((s) => {
                const isWinner = s.rank <= WINNER_CUTOFF;
                return (
                  <tr
                    key={s.userId}
                    className={`border-b border-white/5 align-top last:border-0 text-white/85 ${
                      isWinner ? 'bg-[#00ff8b]/[0.06]' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <span className={isWinner ? 'font-semibold text-[#5bffb4]' : 'text-white/70'}>
                        {s.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => onOpenUser(s.userId)}
                        title="Open this player's user page"
                        className="text-left transition hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      >
                        <div className="font-medium text-white">{s.displayName || '—'}</div>
                        <div className="text-xs text-white/50 [overflow-wrap:anywhere]">{s.email}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white/70">{s.phone || '—'}</td>
                    <td className="px-4 py-3 tabular-nums">{s.highScore.toLocaleString('en-US')}</td>
                    <td className="px-4 py-3 text-white/60">
                      {s.achievedAt ? new Date(s.achievedAt).toLocaleString('en-US') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setPendingDq(s)}
                        className="rounded-full border border-[#c0344f]/50 px-3 py-1 text-xs text-[#ff8da6] transition hover:bg-[#c0344f]/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      >
                        Disqualify
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disabled={loading}
        itemLabel="entries"
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      {excluded.length > 0 && (
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wide text-white/70">
            Disqualified this week
          </h3>
          <div className="mt-3 overflow-x-auto rounded-xl border border-white/15 bg-black/20">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/55">
                  <Th>Player</Th>
                  <Th>Score</Th>
                  <Th>Reason</Th>
                  <Th>By</Th>
                  <Th>When</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {excluded.map((x) => (
                  <tr key={x.userId} className="border-b border-white/5 align-top last:border-0 text-white/85">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => onOpenUser(x.userId)}
                        title="Open this player's user page"
                        className="text-left transition hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      >
                        <div className="font-medium text-white">{x.displayName || '—'}</div>
                        <div className="text-xs text-white/50 [overflow-wrap:anywhere]">{x.email}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      {x.highScore == null ? '—' : x.highScore.toLocaleString('en-US')}
                    </td>
                    <td className="px-4 py-3 text-white/75">{x.reason}</td>
                    <td className="px-4 py-3 text-white/60 [overflow-wrap:anywhere]">
                      {x.excludedByEmail || '—'}
                    </td>
                    <td className="px-4 py-3 text-white/60">
                      {x.excludedAt ? new Date(x.excludedAt).toLocaleDateString('en-US') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        disabled={reinstatingId === x.userId}
                        onClick={() => void onReinstate(x.userId)}
                        className="rounded-full border border-[#00ff8b]/40 px-3 py-1 text-xs text-[#5bffb4] transition hover:bg-[#00ff8b]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {reinstatingId === x.userId ? 'Working…' : 'Reinstate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendingDq && week != null && (
        <DisqualifyModal
          row={pendingDq}
          week={week}
          onClose={() => setPendingDq(null)}
          onDone={() => {
            setPendingDq(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function DisqualifyModal({
  row,
  week,
  onClose,
  onDone,
}: {
  row: AdminStandingRow;
  week: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const ok = reason.trim().length >= 3;

  const confirm = async (): Promise<void> => {
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await disqualifyUser(row.userId, week, reason.trim());
      onDone();
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : 'Could not disqualify. Please try again.');
    }
  };

  return (
    <Modal title={`Disqualify from Week ${week}`} onClose={onClose} disabled={busy}>
      <p className="mt-3 text-sm text-white/80">
        Remove <span className="text-white">{row.displayName || row.email}</span> (score{' '}
        {row.highScore.toLocaleString('en-US')}) from Week {week}&rsquo;s public leaderboard. This is
        reversible.
      </p>
      <label className="mt-4 flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
        Reason
        <textarea
          ref={ref}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={255}
          className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          placeholder="Recorded in the audit log"
        />
      </label>
      {error && <p className="mt-3 text-sm text-[#ff8da6]">{error}</p>}
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-50 sm:min-w-[120px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy || !ok}
          className="rounded-full bg-[#c0344f] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#d83c5a] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[160px]"
        >
          {busy ? 'Working…' : 'Disqualify'}
        </button>
      </div>
    </Modal>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-4 py-2 font-heading text-xs uppercase tracking-wide">{children}</th>;
}
